import { Box, MenuItem, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo } from "react";
import { CarSelectField } from "@app/components/Inputs";
import { useSimulationABTestYields } from "./useSimulationABTestYields";
import { CarDownloadButton } from "@app/components/Buttons";
import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { SimulationABTestPercentileTable } from "./SimulationABTestPercentileTable";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";

interface SimulationABTestYieldsProps {
  simulationJobIdA: string;
  simulationJobIdB: string;
}

const timeHorizonItems: { id: string; label: string }[] = [
  {
    id: "short",
    label: "Short",
  },
  {
    id: "extended",
    label: "Extended",
  },
];

export const SimulationABTestYields = memo(
  (props: SimulationABTestYieldsProps) => {
    const [timeHorizon, setTimeHorizon] = useLocalStorage<string | undefined>(
      "SimulationABTestYields_timeHorizon",
      "short",
    );

    const data = useSimulationABTestYields({
      simulationJobIdA: props.simulationJobIdA,
      simulationJobIdB: props.simulationJobIdB,
    });

    const pageContent = usePageContent();

    return (
      <TablePlaceHolder
        sx={{ mx: 7, mt: 5 }}
        isLoading={data.assetClassSelector.isLoading || pageContent.isLoading}
        rows={8}
      >
        <CarPageContent
          sx={{ mb: 3 }}
          content={pageContent.getContent(
            CarPageContentCode.SIMULATION_ANALYSIS_AB_TEST_YIELDS,
          )}
          isWhiteContext
        />
        <Box sx={{ mt: 1, display: "flex", gap: 5, alignItems: "flex-end" }}>
          <CarAssetClassSelector
            label="Asset Class"
            sx={{
              width: 250,
              ".MuiInputLabel-root": {
                fontSize: "16px",
              },
              ".MuiOutlinedInput-root": {
                marginTop: "28px",
                bgcolor: "white",
              },
            }}
            {...data.assetClassSelector}
          />
          <CarSelectField
            label="Time Horizon"
            sx={{
              width: 216,
              ".MuiInputLabel-root": {
                fontSize: "16px",
              },
              ".MuiOutlinedInput-root": {
                marginTop: "28px",
                bgcolor: "white",
              },
            }}
            value={timeHorizon}
            onChange={setTimeHorizon}
          >
            {timeHorizonItems.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.label}
              </MenuItem>
            ))}
          </CarSelectField>
          <CarDownloadButton
            sx={{ ml: "auto", mb: 0.5 }}
            onClick={data.handleDownloadReports}
          >
            Download Reports
          </CarDownloadButton>
        </Box>
        <TablePlaceHolder sx={{ mt: 5 }} isLoading={data.isFetching} rows={5}>
          {data.assetClassSelector.value && (
            <>
              <Typography variant="subhSemiBold" sx={{ mt: 5, mb: 1 }}>
                {data.assetClassName}
              </Typography>
              <SimulationABTestPercentileTable
                table={data.table}
                isExtended={timeHorizon === "extended"}
                onValueClick={data.handleValueClick}
              />
              <Typography variant="subhSemiBold" sx={{ mt: 5, mb: 1 }}>
                Inflation
              </Typography>
              <SimulationABTestPercentileTable
                table={data.inflationTable}
                isExtended={timeHorizon === "extended"}
                onValueClick={data.handleValueClick}
              />
            </>
          )}
        </TablePlaceHolder>
      </TablePlaceHolder>
    );
  },
);
